/**
 * Patient Access (PAC) warm hand-off script — the beats a rep works during the call
 * that follows the NP consult.
 *
 * The PAC joins the last 15 minutes of the consult, or calls back shortly after,
 * to turn a completed consult into a booked, paid procedure date.
 *
 * ENG-OWNED, like `patient-access-script.ts`: a typed definition in the repo rather
 * than a FHIR Questionnaire, so wording changes go through a PR. Content is
 * transcribed from `everself-design-system/mockups/orbit-ci-warm-handoff-card.html`,
 * which is the source of truth for copy.
 *
 * SCRIPT LINES ARE STATIC. `[patient name]`, `[NP name]`, `[physician]`,
 * `[date 1]` and `[date 2]` render literally, so the team reads the same sentence
 * on every call. Only chart FIELDS carry live values — here, the procedure dates.
 *
 * NO FREE TEXT. Every input is a select, checkbox or chip.
 *
 * BOTH OUTCOME BRANCHES SHIP AT ONCE. `ready` and `notready` are sections, not
 * states: the rep works the one the patient lands on and leaves the other
 * untouched, the same way both openings ship on the intro-call card.
 */

import { PacBeat, PAYMENT_OPTIONS } from './patient-access-script';

/**
 * The chip that lets a patient decline to name a date.
 *
 * Deliberately an option rather than "leave it blank": an empty field cannot be
 * told apart from a question nobody asked, and "would not commit" is a fact the
 * CC team acts on.
 */
export const NOT_READY_TO_COMMIT = 'Not ready to commit yet';

/** How soon to call back, for a patient who is not ready to proceed. */
export const RECONNECT_WHEN_OPTIONS = [
  'In a few days',
  'Early next week',
  'Late next week',
  'They will call us',
] as const;

/** Why a patient is hesitating. Internal — not read aloud. */
export const HANDOFF_BARRIER_OPTIONS = [
  'Pricing/financing',
  'Timing',
  'Talking to family',
  'Competitor',
  'Efficacy',
  'Safety/risks',
  'None given',
] as const;

export const HANDOFF_OUTCOME_OPTIONS = [
  'Payment completed',
  'Ready to pay — link sent',
  'Follow-up scheduled',
  'Follow-up needed',
  'Not ready to proceed',
  'Not interested',
] as const;
export type HandoffOutcome = (typeof HANDOFF_OUTCOME_OPTIONS)[number];

/**
 * The one outcome that closes the payment order and moves the patient on.
 *
 * Only a completed payment counts — `Ready to pay — link sent` explicitly does
 * not, because a sent link is not money received. Everything else leaves the
 * patient in `post-consultation` with the drip campaign still running.
 */
export function isPaidOutcome(outcome: string | undefined): boolean {
  return outcome === 'Payment completed';
}

/* -------------------------------------------------------------------------
   Long-form script copy. Patient-facing register.
   ------------------------------------------------------------------------- */

const GREET =
  'Hi [patient name], this is [rep] from Everself. We spoke earlier when I helped schedule your consultation.';

const CHECK_IN =
  'How did your conversation with [NP name] go? Did any questions come up afterward that we can help with?';

const PURPOSE = 'I’m here to help with the next steps and make the procedure-booking process as easy as possible.';

/**
 * Names two dates, though the grid offers three plus the decline option.
 * Reading three dates aloud is unwieldy; the closing clause covers the rest.
 */
const TIMING =
  'It looks like Dr. [physician] has availability on [date 1] and [date 2]. Would either of those work for you, or is there another timeframe you had in mind?';

const PAYMENT =
  'Great. The next step is to take care of the payment so we can reserve your procedure date. We accept HSA and FSA funds and also offer monthly financing. Do you have a sense of which option you’d like to use?';

const STAY_ON_LINE =
  'You should receive a secure payment link from us shortly. I’m happy to stay on the line while you complete it in case you have any questions or run into any issues.';

const RECONNECT =
  'No problem at all. Would it be helpful for us to reconnect in a few days, or would sometime next week be better?';

const CLOSE_PAID =
  'You’re all set — your procedure date is reserved and our care team will be in touch to confirm the details.';

const CLOSE_UNPAID = 'I’ll leave the link open for you. Give me a call any time if a question comes up.';

/* -------------------------------------------------------------------------
   Beats
   ------------------------------------------------------------------------- */

export const PAC_WARM_HANDOFF_SCRIPT: readonly PacBeat[] = [
  {
    id: 'greet',
    title: 'Greeting',
    items: [
      { kind: 'checkbox', id: 'g_hi', label: 'Reintroduce yourself', say: GREET },
      { kind: 'checkbox', id: 'g_check', label: 'Check in on the consult', say: CHECK_IN },
      { kind: 'checkbox', id: 'g_purpose', label: 'State why you are calling', say: PURPOSE },
    ],
  },
  {
    id: 'timing',
    title: 'Discuss timing',
    items: [
      { kind: 'script', text: TIMING },
      // Days come from the physician's block schedule at the patient's location.
      // Selecting one records the date the patient agreed to; it books nothing,
      // because the CC team schedules once payment completes.
      { kind: 'procedureDateGrid', id: 't_date' },
    ],
  },
  {
    id: 'ready',
    title: 'Ready to move ahead',
    items: [
      { kind: 'select', id: 'r_pay', label: 'Payment option', options: PAYMENT_OPTIONS, say: PAYMENT },
      {
        kind: 'group',
        items: [
          {
            kind: 'actionLink',
            label: 'Create a payment order',
            linkText: 'Create payment order',
            action: 'openPaymentOrder',
            say: STAY_ON_LINE,
          },
          { kind: 'checkbox', id: 'r_stayed', label: 'Stayed on the line while they paid' },
          {
            kind: 'warning',
            text: 'Never read back or record card details. The link is the only payment path.',
          },
        ],
      },
    ],
  },
  {
    id: 'notready',
    title: 'Not ready yet',
    items: [
      { kind: 'chips', id: 'n_when', label: 'Reconnect when', options: RECONNECT_WHEN_OPTIONS, say: RECONNECT },
      {
        kind: 'multiChips',
        id: 'n_why',
        label: 'What is holding them back',
        options: HANDOFF_BARRIER_OPTIONS,
      },
    ],
  },
  {
    id: 'close',
    title: 'Close',
    items: [
      { kind: 'select', id: 'close', label: 'Outcome', options: HANDOFF_OUTCOME_OPTIONS },
      { kind: 'conditionalScript', condition: 'If paid —', text: CLOSE_PAID },
      { kind: 'conditionalScript', condition: 'If not —', text: CLOSE_UNPAID },
    ],
  },
];

/** Beat ids, in order. */
export const PAC_WARM_HANDOFF_BEAT_IDS = PAC_WARM_HANDOFF_SCRIPT.map((beat) => beat.id);
